import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/contexts/AuthContext";
import SessionTracker from "@/components/SessionTracker";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import Index from "./pages/Index";

// Lazy load all pages except Index for code splitting
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Settings = lazy(() => import("./pages/Settings"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PresentsList = lazy(() => import("./pages/PresentsList"));
const SecretSanta = lazy(() => import("./pages/SecretSanta"));
const SecretSantaGroupDetail = lazy(() => import("./pages/SecretSantaGroupDetail"));
const ElfPlanner = lazy(() => import("./pages/ElfPlanner"));
const ChristmasStories = lazy(() => import("./pages/ChristmasStories"));
const LetterToSanta = lazy(() => import("./pages/LetterToSanta"));
const NaughtyNice = lazy(() => import("./pages/NaughtyNice"));
const Certificates = lazy(() => import("./pages/Certificates"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ChristmasPhotos = lazy(() => import("./pages/ChristmasPhotos"));
const Admin = lazy(() => import("./pages/Admin"));
const CallSanta = lazy(() => import("./pages/CallSanta"));
const SantaVideo = lazy(() => import("./pages/SantaVideo"));
const About = lazy(() => import("./pages/About"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Terms = lazy(() => import("./pages/Terms"));
const Contact = lazy(() => import("./pages/Contact"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const SecretSantaWishlist = lazy(() => import("./pages/SecretSantaWishlist"));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <SessionTracker />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about" element={<About />} />
              <Route path="/features" element={<Features />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/presents" element={<PresentsList />} />
              <Route path="/secret-santa" element={<SecretSanta />} />
              <Route path="/secret-santa/group/:groupId" element={<SecretSantaGroupDetail />} />
              <Route path="/secret-santa/wishlist/:token" element={<SecretSantaWishlist />} />
              <Route path="/elf-planner" element={<ElfPlanner />} />
              <Route path="/stories" element={<ChristmasStories />} />
              <Route path="/santa-letter" element={<LetterToSanta />} />
              <Route path="/naughty-nice" element={<NaughtyNice />} />
              <Route path="/certificates" element={<Certificates />} />
              <Route path="/photos" element={<ChristmasPhotos />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/call-santa" element={<CallSanta />} />
              <Route path="/santa-video" element={<SantaVideo />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
